import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block cursor-pointer">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);
  const category = getCategoryBySlug(article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '泷泽萝拉官方资料站',
      url: 'https://readdy.ai/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://readdy.ai/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: category?.name || article.category, to: `/category/${article.category}` },
              { label: article.title },
            ]}
            categorySlug={article.category}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-800 font-label whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
              <span>{article.author}</span>
              <span>&middot;</span>
              <span>发布于 {article.publishDate}</span>
              <span>&middot;</span>
              <span>更新于 {article.updateDate}</span>
              <span>&middot;</span>
              <span>阅读约 {article.readTime} 分钟</span>
            </div>
          </header>

          {/* Cover Image */}
          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 泷泽萝拉`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 max-w-none lg:max-w-[780px]">
              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-950
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:my-4 prose-li:text-foreground-700 prose-li:mb-2
                  prose-strong:text-foreground-900
                  [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4
                  [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6
                  [&_li]:text-foreground-700 [&_li]:mb-1.5
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {/* Sidebar */}
            <aside className="lg:w-[300px] flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  相关文章
                </h3>
                <div className="space-y-2">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.slug} article={related} variant="compact" />
                  ))}
                  {relatedArticles.length === 0 && (
                    <p className="text-sm text-foreground-500">暂无相关文章</p>
                  )}
                </div>

                <div className="mt-8 p-4 bg-background-100 rounded-xl">
                  <h4 className="text-sm font-label font-semibold text-foreground-900 mb-2">
                    分享这篇文章
                  </h4>
                  <div className="flex items-center gap-2">
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                      <i className="ri-twitter-x-line" />
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                      <i className="ri-facebook-line" />
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                      <i className="ri-link" />
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}